import type { SurveyV2 } from '@act/shared/data-transfer-objects';

/** Form field value for a survey question (text, single option id string, or multi option ids). */
export type SurveyQuestionFormValue = string | number[] | null;

/**
 * Stable key for a question's saved response content (not object identity).
 * Used so react-hook-form `reset` only runs when the persisted answer changes,
 * not when Redux replaces the assigned-survey graph with equivalent data.
 *
 * @param questionResponse - Response for the active question, if any
 * @returns Sync key string
 */
export function buildSurveyQuestionResponseSyncKey(
  questionResponse: SurveyV2.QuestionResponseDto | null | undefined
): string {
  if (!questionResponse || questionResponse.skipped) {
    return 'empty';
  }

  if (questionResponse.selectedOptionIds?.length) {
    return `o:${questionResponse.selectedOptionIds.map(Number).join(',')}`;
  }

  return `t:${questionResponse.textResponse ?? ''}`;
}

/**
 * Form `value` for a question from its saved response.
 * Checkbox / multiselect use `number[]` (empty when unanswered).
 *
 * @param questionType - Survey question type
 * @param questionResponse - Saved response, if any
 * @returns Initial form value
 */
export function getSurveyQuestionFormValue(
  questionType: string,
  questionResponse: SurveyV2.QuestionResponseDto | null | undefined
): SurveyQuestionFormValue {
  const isMultiOption =
    questionType === 'checkbox' || questionType === 'multiselect';

  if (!questionResponse || questionResponse.skipped) {
    return isMultiOption ? [] : null;
  }

  if (questionResponse.selectedOptionIds?.length > 0) {
    if (isMultiOption) {
      return questionResponse.selectedOptionIds.map(Number);
    }
    return String(questionResponse.selectedOptionIds[0]);
  }

  if (isMultiOption) {
    return [];
  }

  return questionResponse.textResponse ?? null;
}

/**
 * Apply dirty/draft tracking for a question when only the active step is mounted.
 * Dirty + draft must survive remounts (Previous / step-header navigation); clear
 * both only when the form reports clean (e.g. after a successful save).
 *
 * @param dirtyQuestions - Question ids with unsaved edits
 * @param drafts - In-progress values keyed by question id
 * @param questionId - Question being updated
 * @param isDirty - Whether the form still has unsaved edits vs the saved response
 * @param draftValue - Current form value when dirty; ignored when clean
 * @returns Next dirty set and draft map
 */
export function applySurveyQuestionFormStateChange(
  dirtyQuestions: ReadonlySet<number>,
  drafts: ReadonlyMap<number, SurveyQuestionFormValue>,
  questionId: number,
  isDirty: boolean,
  draftValue?: SurveyQuestionFormValue
): {
  dirtyQuestions: Set<number>;
  drafts: Map<number, SurveyQuestionFormValue>;
} {
  const nextDirty = new Set(dirtyQuestions);
  const nextDrafts = new Map(drafts);

  if (isDirty) {
    nextDirty.add(questionId);
    if (draftValue !== undefined) {
      nextDrafts.set(questionId, draftValue);
    }
  } else {
    nextDirty.delete(questionId);
    nextDrafts.delete(questionId);
  }

  return { dirtyQuestions: nextDirty, drafts: nextDrafts };
}
